import type { ActionReturn } from 'svelte/action';

interface ScrollPositionDetail {
	offsetPercentage: number;
	currentScroll: number;
}

interface Attributes {
	onchangescroll: (e: CustomEvent<ScrollPositionDetail>) => void;
}

export function scrollPosition(node: HTMLElement): ActionReturn<undefined, Attributes> {
	let nodeHeight: number;
	let maxScroll: number;
	let rafId: number | null = null;

	function setOffsetPercentage() {
		const currentScroll = maxScroll - window.scrollY;
		const offsetPercentage = Math.min(
			100,
			Math.max(-100, (currentScroll / nodeHeight) * 100)
		);

		node.dispatchEvent(
			new CustomEvent('changescroll', { detail: { offsetPercentage, currentScroll } })
		);
	}

	function handleScroll() {
		if (rafId) return;
		rafId = requestAnimationFrame(() => {
			setOffsetPercentage();
			rafId = null;
		});
	}

	function updateDimensions() {
		nodeHeight = node.clientHeight;
		maxScroll = node.getBoundingClientRect().top + window.scrollY;
	}

	// Initial setup
	node.style.setProperty('minHeight', `${node.clientHeight}px`);
	updateDimensions();
	setOffsetPercentage();

	// Setup resize observer to update dimensions when node size changes
	const resizeObserver = new ResizeObserver(() => {
		updateDimensions();
		setOffsetPercentage();
	});
	resizeObserver.observe(node);

	const observer = new IntersectionObserver(
		(entries) => {
			const entry = entries[0];
			if (entry.isIntersecting) {
				window.addEventListener('scroll', handleScroll, { passive: true });
			} else {
				window.removeEventListener('scroll', handleScroll);
			}
		},
		{
			rootMargin: '50% 0px', // Start observing before the element comes into view
			threshold: 0
		}
	);

	observer.observe(node);

	return {
		destroy() {
			observer.disconnect();
			resizeObserver.disconnect();
			window.removeEventListener('scroll', handleScroll);
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
		}
	};
}
